import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const PALETTES = [
    { c1: "#e8c470", c2: "#c98a3e" },
    { c1: "#d98a6a", c2: "#b05a3c" },
    { c1: "#f3e5ab", c2: "#d4af37" },
    { c1: "#cfe0f0", c2: "#8ab4d8" },
];

const Butterfly = ({ b }) => (
    <motion.div
        className="butterfly"
        style={{ width: b.size, left: 0, top: 0 }}
        data-testid="butterfly-flying"
        initial={{ x: b.startX, y: b.startY, opacity: 0, rotate: b.fromLeft ? 12 : -12 }}
        animate={{
            x: [b.startX, b.midX, b.endX],
            y: [b.startY, b.midY, b.endY],
            opacity: [0, 1, 1, 0.9, 0],
            rotate: b.fromLeft ? [12, -8, 10] : [-12, 8, -10],
        }}
        exit={{ opacity: 0 }}
        transition={{ duration: 11, times: [0, 0.5, 1], ease: "easeInOut" }}
    >
        <svg viewBox="0 0 40 36">
            <g className="wing wing-left">
                <ellipse cx="11" cy="13" rx="10" ry="13" fill={b.c1} opacity="0.95" />
                <ellipse cx="13" cy="27" rx="6" ry="7" fill={b.c2} opacity="0.9" />
            </g>
            <g className="wing wing-right">
                <ellipse cx="29" cy="13" rx="10" ry="13" fill={b.c1} opacity="0.95" />
                <ellipse cx="27" cy="27" rx="6" ry="7" fill={b.c2} opacity="0.9" />
            </g>
            <rect x="18.6" y="5" width="2.8" height="26" rx="1.4" fill="#3a2c14" />
        </svg>
    </motion.div>
);

const Firefly = ({ b }) => (
    <motion.div
        className="butterfly"
        style={{ left: 0, top: 0 }}
        data-testid="firefly-flying"
        initial={{ x: b.startX, y: b.startY, opacity: 0 }}
        animate={{
            x: [b.startX, b.midX, b.midX2, b.endX],
            y: [b.startY, b.midY, b.midY2, b.endY],
            opacity: [0, 1, 1, 0],
        }}
        exit={{ opacity: 0 }}
        transition={{ duration: 9, ease: "easeInOut" }}
    >
        <div className="firefly-dot" />
    </motion.div>
);

export default function Butterflies() {
    const [active, setActive] = useState([]);
    const counter = useRef(0);

    useEffect(() => {
        let last = 0;
        let count = 0;
        const timers = new Set();

        const spawn = () => {
            const now = Date.now();
            if (now - last < 1800) return;
            if (count >= 2) return;
            last = now;
            count += 1;
            counter.current += 1;
            const isFirefly = counter.current % 2 === 0;
            const w = window.innerWidth;
            const h = window.innerHeight;
            let b;

            if (isFirefly) {
                const startX = w * (0.1 + Math.random() * 0.8);
                const startY = h * (0.6 + Math.random() * 0.25);
                b = {
                    id: now,
                    type: "firefly",
                    startX,
                    startY,
                    midX: startX + (Math.random() - 0.5) * 260,
                    midY: startY - 60 - Math.random() * 120,
                    midX2: startX + (Math.random() - 0.5) * 360,
                    midY2: startY - 20 - Math.random() * 160,
                    endX: startX + (Math.random() - 0.5) * 300,
                    endY: h * (0.5 + Math.random() * 0.3),
                    life: 9500,
                };
            } else {
                const fromLeft = Math.random() > 0.5;
                const startX = fromLeft ? -70 : w + 70;
                const startY = h * (0.3 + Math.random() * 0.45);
                const endX = fromLeft ? w + 70 : -70;
                const endY = h * (0.25 + Math.random() * 0.45);
                const palette = PALETTES[Math.floor(Math.random() * PALETTES.length)];
                b = {
                    id: now,
                    type: "butterfly",
                    fromLeft,
                    startX,
                    startY,
                    endX,
                    endY,
                    midX: (startX + endX) / 2 + (Math.random() - 0.5) * 320,
                    midY: Math.min(startY, endY) - 90 - Math.random() * 140,
                    size: 24 + Math.random() * 16,
                    life: 11500,
                    ...palette,
                };
            }

            const t = setTimeout(() => {
                count -= 1;
                setActive((l) => l.filter((x) => x.id !== b.id));
                timers.delete(t);
            }, b.life);
            timers.add(t);
            setActive((l) => [...l, b]);
        };

        window.addEventListener("scroll", spawn, { passive: true });
        return () => {
            window.removeEventListener("scroll", spawn);
            timers.forEach((t) => clearTimeout(t));
        };
    }, []);

    return (
        <div className="butterfly-layer" data-testid="butterfly-layer" aria-hidden="true">
            <AnimatePresence>
                {active.map((b) =>
                    b.type === "firefly" ? <Firefly key={b.id} b={b} /> : <Butterfly key={b.id} b={b} />
                )}
            </AnimatePresence>
        </div>
    );
}
